import i18n from "i18next";
import { Locale, sv, enGB, de, fr } from "date-fns/locale";
import { LanguageCode } from "../api";

import {
  I18N_LANGUAGES,
  getI18nLanguage,
  languageCodeFromTag,
  type I18nLanguage,
} from "./language-codes";

// The map itself lives in `language-codes.ts`, which imports nothing, so the
// embedded widgets can share it without dragging the locale tables above into
// every host page. Re-exported here because this is where the product reads it.
export { I18N_LANGUAGES, getI18nLanguage };
export type { I18nLanguage };

// Map of language codes -> date-fns locale objects
const dateFnsLocaleMap: Record<LanguageCode, Locale> = {
  "en-GB": enGB,
  "sv-SE": sv,
  "de-DE": de,
  "fr-FR": fr,
};

/**
 * The language code for an i18n language or a browser tag, defaulting to the
 * one i18next currently serves.
 */
export const getLanguageCode = (language = i18n.language): LanguageCode =>
  // i18n.language is unset until init resolves, and a component that renders
  // first would otherwise throw rather than fall back.
  languageCodeFromTag(language);

/**
 * The site's own text for a guest's language, or `undefined`.
 *
 * Picks an *entry* and then leaves its fields alone, rather than resolving each
 * field on its own. A half-filled entry is a real state — the migration
 * produces one when a site customised only its heading, and the API accepts one
 * — and resolving per field would answer the heading from the guest's language
 * and the description from the English override, putting two languages on one
 * card. A missing half is better answered by the caller's own built-in copy,
 * which exists in every language the engine serves.
 *
 * The guest's language first, then English, then nothing. No Swedish tail,
 * unlike the chain the ad surfaces use: English earns its hop because a
 * customer's English notice may carry specifics — an opening date, a phone
 * number — that our generic copy cannot, while Swedish would just be a card the
 * reader cannot read.
 */
export const resolveEntry = <T extends { languageCode: LanguageCode }>(
  entries: readonly T[] | undefined,
  language: LanguageCode = getLanguageCode(),
): T | undefined => {
  const find = (code: LanguageCode) =>
    entries?.find((candidate) => candidate.languageCode === code);
  return find(language) ?? find("en-GB");
};

/**
 * Returns the appropriate date-fns Locale based on the current or provided i18n language.
 */
export const getDateFnsLocale = (): Locale => {
  const langCode = getLanguageCode();
  return dateFnsLocaleMap[langCode] ?? enGB;
};
